import { useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useTenant } from "@/lib/tenant-context.js";
import { useAsync } from "@/hooks/use-async.js";
import { useKeyboardShortcuts } from "@/hooks/use-keyboard-shortcuts.js";
import { Button } from "@/components/ui/button.js";
import { Input } from "@/components/ui/input.js";
import { Label } from "@/components/ui/label.js";
import { PageHeader } from "@/components/ui/page-header.js";
import { Select } from "@/components/ui/select.js";
import { Skeleton } from "@/components/ui/skeleton.js";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog.js";
import { Badge } from "@/components/ui/badge.js";
import { useToast } from "@/components/ui/toast.js";
import { DataTable } from "@/views/data-table.js";
import type { Service } from "@/lib/types.js";

export function ServicesPage(): React.JSX.Element {
  const { api, me } = useTenant();
  const { toast } = useToast();
  const services = useAsync(() => api.listServices(), [api]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Service | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Tenant-level catalog lock: the CRM owns the whole catalog once a sync has
  // applied (catalog_version > 0). Authoring UI is hidden — the API rejects
  // those writes with 409 catalog_managed_by_crm anyway.
  const managedByCrm = me?.tenant.catalog_managed_by_crm === true;

  const rows = services.data?.data ?? [];
  useKeyboardShortcuts({
    c: () => {
      if (!managedByCrm) setCreating(true);
    },
    j: () => setSelectedId(moveSelection(rows, selectedId, 1)),
    k: () => setSelectedId(moveSelection(rows, selectedId, -1)),
    e: () => {
      if (managedByCrm) return;
      const row = rows.find((s) => s.id === selectedId);
      if (row) setEditing(row);
    }
  });

  return (
    <div className="flex flex-col">
      <PageHeader
        eyebrow="Catalog"
        title="Services"
        description="Bookable offerings. Define duration, routing engine, deposits, and aliases."
        actions={
          managedByCrm ? undefined : (
            <Button onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" />
              Add service
            </Button>
          )
        }
      />
      {managedByCrm && (
        <div
          role="note"
          aria-label="CRM ownership"
          className="mb-3 rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-xs text-muted-foreground"
        >
          Managed in the CRM — edit services and doctors there; changes sync here automatically.
        </div>
      )}
      {services.loading ? (
        <Skeleton className="h-72 w-full" />
      ) : (
        <DataTable<Service>
          rows={rows}
          getRowKey={(s) => s.id}
          selectedRowKey={selectedId}
          onRowClick={(s) => setSelectedId(s.id)}
          columns={[
            { header: "Name", cell: (s) => s.name },
            { header: "Duration", cell: (s) => `${s.duration_minutes}m` },
            {
              header: "Engine",
              cell: (s) => (
                <Badge variant={s.service_location === "at_customer" ? "warning" : "outline"}>
                  {s.service_location === "at_customer" ? "Routed" : "Slot grid"}
                </Badge>
              )
            },
            {
              header: "Deposit",
              cell: (s) => (s.deposit_amount_cents > 0 ? `$${(s.deposit_amount_cents / 100).toFixed(2)}` : "—")
            },
            // Row edit/delete actions disappear entirely for CRM-managed
            // catalogs (read-only display stays).
            ...(managedByCrm ? [] : [{
              header: "",
              className: "w-20 text-right",
              cell: (s: Service) => (
                <div className="flex justify-end gap-0.5">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditing(s);
                    }}
                    aria-label={`Edit ${s.name}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Delete ${s.name}`}
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await api.deleteService(s.id);
                        toast({ title: "Service archived", variant: "success" });
                        services.reload();
                      } catch (error) {
                        toast({ title: "Could not delete", description: String(error), variant: "destructive" });
                      }
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )
            }])
          ]}
        />
      )}
      <ServiceDialog
        open={creating}
        onOpenChange={setCreating}
        mode="create"
        onSaved={() => {
          services.reload();
          setCreating(false);
        }}
      />
      <ServiceDialog
        open={editing !== null}
        onOpenChange={(v) => !v && setEditing(null)}
        mode="edit"
        initial={editing}
        onSaved={() => {
          services.reload();
          setEditing(null);
        }}
      />
    </div>
  );
}

function moveSelection<T extends { id: string }>(
  rows: T[],
  current: string | null,
  delta: 1 | -1
): string | null {
  if (rows.length === 0) return null;
  const idx = current ? rows.findIndex((r) => r.id === current) : -1;
  const nextIdx = idx === -1 ? (delta === 1 ? 0 : rows.length - 1) : Math.max(0, Math.min(rows.length - 1, idx + delta));
  return rows[nextIdx]!.id;
}

/**
 * Service create + edit dialog. The server uses POST for create and PATCH for
 * partial update; this component bridges both modes against a single form.
 */
function ServiceDialog({
  open,
  onOpenChange,
  mode,
  initial,
  onSaved
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  mode: "create" | "edit";
  initial?: Service | null;
  onSaved: () => void;
}): React.JSX.Element {
  const { api } = useTenant();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "New service" : `Edit ${initial?.name ?? "service"}`}</DialogTitle>
        </DialogHeader>
        <form
          className="grid grid-cols-1 gap-3 sm:grid-cols-2"
          onSubmit={async (event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            const body: Partial<Service> = {
              name: String(form.get("name") ?? ""),
              duration_minutes: Number(form.get("duration_minutes") ?? 30),
              service_location: form.get("service_location") === "at_customer" ? "at_customer" : "at_business",
              required_resource_role: String(form.get("required_resource_role") ?? "provider"),
              deposit_amount_cents: Math.round(Number(form.get("deposit_amount_dollars") ?? 0) * 100),
              no_show_fee_cents: Math.round(Number(form.get("no_show_fee_dollars") ?? 0) * 100),
              // PRD availability-rules: numeric availability fields are surfaced
              // as inputs; defaults match the server defaults (0/0/0/90/15).
              buffer_before_minutes: Number(form.get("buffer_before_minutes") ?? 0),
              buffer_after_minutes: Number(form.get("buffer_after_minutes") ?? 0),
              min_lead_minutes: Number(form.get("min_lead_minutes") ?? 0),
              max_horizon_days: Number(form.get("max_horizon_days") ?? 90),
              slot_increment_minutes: Number(form.get("slot_increment_minutes") ?? 15) as Service["slot_increment_minutes"],
              aliases: String(form.get("aliases") ?? "")
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            };
            setSubmitting(true);
            try {
              if (mode === "edit" && initial) {
                await api.updateService(initial.id, body);
                toast({ title: "Service updated", variant: "success" });
              } else {
                await api.createService(body);
                toast({ title: "Service created", variant: "success" });
              }
              onSaved();
            } catch (error) {
              toast({ title: "Could not save", description: String(error), variant: "destructive" });
            } finally {
              setSubmitting(false);
            }
          }}
        >
          <Field label="Name" name="name" required defaultValue={initial?.name ?? ""} />
          <Field label="Duration (min)" name="duration_minutes" type="number" defaultValue={String(initial?.duration_minutes ?? 30)} />
          <Field label="Aliases (comma)" name="aliases" placeholder="cut, trim" defaultValue={(initial?.aliases ?? []).join(", ")} />
          <div className="flex flex-col gap-1">
            <Label htmlFor="service_location">Engine</Label>
            <Select id="service_location" name="service_location" defaultValue={initial?.service_location ?? "at_business"}>
              <option value="at_business">Slot grid (at_business)</option>
              <option value="at_customer">Routed (at_customer)</option>
            </Select>
          </div>
          <Field label="Required resource role" name="required_resource_role" defaultValue={initial?.required_resource_role ?? "provider"} />
          <Field label="Deposit ($)" name="deposit_amount_dollars" type="number" step="0.01" defaultValue={String(((initial?.deposit_amount_cents ?? 0) / 100).toFixed(2))} />
          <Field label="No-show fee ($)" name="no_show_fee_dollars" type="number" step="0.01" defaultValue={String(((initial?.no_show_fee_cents ?? 0) / 100).toFixed(2))} />
          <Field
            label="Buffer before (min)"
            name="buffer_before_minutes"
            type="number"
            min={0}
            max={240}
            defaultValue={String(initial?.buffer_before_minutes ?? 0)}
          />
          <Field
            label="Buffer after (min)"
            name="buffer_after_minutes"
            type="number"
            min={0}
            max={240}
            defaultValue={String(initial?.buffer_after_minutes ?? 0)}
          />
          <Field
            label="Min lead (min)"
            name="min_lead_minutes"
            type="number"
            min={0}
            max={60 * 24 * 7}
            defaultValue={String(initial?.min_lead_minutes ?? 0)}
          />
          <Field
            label="Max horizon (days)"
            name="max_horizon_days"
            type="number"
            min={1}
            max={365}
            defaultValue={String(initial?.max_horizon_days ?? 90)}
          />
          <div className="flex flex-col gap-1">
            <Label htmlFor="slot_increment_minutes">Slot increment (min)</Label>
            <Select
              id="slot_increment_minutes"
              name="slot_increment_minutes"
              defaultValue={String(initial?.slot_increment_minutes ?? 15)}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="60">60</option>
            </Select>
          </div>
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving…" : mode === "create" ? "Create" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  name,
  ...rest
}: { label: string; name: string } & React.InputHTMLAttributes<HTMLInputElement>): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={name}>{label}</Label>
      <Input id={name} name={name} {...rest} />
    </div>
  );
}
